import React from "react";

import StaticContainer from "../../../layouts/StaticContainer";
import Meta from "../../../layouts/Meta";
import Info from "../../../components/our-team/[name]/Info";
import { getCmsTeam, getCmsTeamMember } from "../../../services/cms/team";
import { TeamMember } from "../../../interfaces";
import { GetStaticPaths, GetStaticProps } from "next";

const MemberDetail = (props: Props) => {
  const { member } = props;

  return (
    <StaticContainer title={member?.cardTitle}>
      <Meta
        title={member?.cardTitle}
        description={member?.cardSubtitle}
        keywords={member?.cardTitle}
        image={member?.cardImage}
      />
      <Info member={member} />
    </StaticContainer>
  );
};

// Member (incl. the rich-HTML bio as cardDetailsHtml) now arrives fully
// resolved from the SOLS Group CMS `publicTeam` node at build time, so there
// is no longer a client-side fetch to merge in after mount.
// Both revalidate windows below are 60s, matching every other CMS-fed page on
// this site. getCmsTeamMember reads the whole publicTeam collection to find one
// member, so every regeneration costs the full roster, and an unknown slug runs
// this getStaticProps as well — a one-second window meant a crawler walking
// made-up names re-triggered that read on every hit. A newly added member can
// 404 for up to a minute; the CMS revalidation webhook covers a real publish.
export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { name }: any = params;
  const member = await getCmsTeamMember(name);

  // A slug with no member behind it must 404. This previously rendered an
  // empty page on a 200 and relied on a client-side router.replace to send
  // the visitor back to the listing, which is a far weaker signal than a
  // 404: crawlers still saw a content-free 200. Members who leave the CMS
  // now return a real 404 instead of lingering as an empty page.
  if (!member) {
    return { notFound: true, revalidate: 60 };
  }

  return {
    props: { member },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const members = await getCmsTeam();
  let paths: any[] = [];

  locales?.forEach((locale) => {
    members?.forEach((member) =>
      paths.push({ params: { name: member.slug }, locale })
    );
  });

  // "blocking", not true: with fallback: true Next answers an unknown slug
  // with a 200 shell before getStaticProps has run, so the notFound above
  // would only take effect on the second request.
  return { paths, fallback: "blocking" };
};

interface Props {
  member: TeamMember;
}

export default MemberDetail;
